﻿import { loadGoogleMapsAPI } from "./googleMapsLoader.js";
import { MarkerClusterer } from "@googlemaps/markerclusterer";

interface CustomMarker extends google.maps.marker.AdvancedMarkerElement {
    id: string;
    icon: string;
}

/**
 * Represents the configuration for a map marker.
 * This interface defines the essential information required to create and manage a marker on a map,
 * including its identifier, visual representation, position, and interactivity.
 * 
 * @property markerId Unique identifier for the marker.
 * @property markerIcon URL or path to the marker's icon image.
 * @property caption Text displayed as the marker's caption or title.
 * @property latitude Geographic latitude coordinate where the marker is placed.
 * @property longitude Geographic longitude coordinate where the marker is placed.
 * @property draggable Indicates whether the marker can be moved by the user.
 */
export interface Coord {
    markerId: string;
    markerIcon: string;
    caption: string;
    latitude: number;
    longitude: number;
    draggable: boolean;
}

export type Route = {
    id: string;
    path: { latitude: number; longitude: number }[];
    strokeColor?: string;
    strokeOpacity?: number;
    strokeWeight?: number;
}

/**
 * Manages a Google Maps instance. This class provides functionalities to initialize,
 * configure, and interact with a map.
 */
export class MapController {
    private mapId: string;
    private googleMapsApiKey: string;
    private googleMapInstance: google.maps.Map | undefined;
    private mapInitializedPromise: Promise<void> | undefined;
    private googleMapMarkerList: Map<string, CustomMarker> = new Map();
    private markerClusterer: MarkerClusterer | undefined;
    private mapClickListener: google.maps.MapsEventListener | undefined;
    private polylinesMap: Map<string, google.maps.Polyline> = new Map();

    /**
     * Constructs a MapController instance.
     * 
     * @param mapId The ID of the DOM element to host the map.
     * @param googleMapsApiKey Google Maps API key for authorization.
     */
    constructor(mapId: string, googleMapsApiKey: string) {
        this.mapId = mapId;
        this.googleMapsApiKey = googleMapsApiKey;
        this.adjustMapHeight = this.adjustMapHeight.bind(this);
        this.handleFullscreenChange = this.handleFullscreenChange.bind(this);
    }

    /**
     * Initializes the map centered at the provided latitude and longitude.
     * 
     * @async
     * @function
     * @param {number} latitude - The latitude of the center point of the map.
     * @param {number} longitude - The longitude of the center point of the map.
     * @param {autoHeightAdjustment} autoHeightAdjustment - The auto height adjustment flag.
     * @returns {Promise<void>} A promise that resolves when the map has been initialized.
     */
    public async init(latitude: number, longitude: number, autoHeightAdjustment: boolean): Promise<any> {
        this.mapInitializedPromise = new Promise<void>((resolve, reject) => {
            const div = document.getElementById(this.mapId) as HTMLElement | null;
            if (!div) {
                console.error("Map div not found");
                reject("Map div not found");
                return;
            }

            loadGoogleMapsAPI(this.googleMapsApiKey).then(() => {

                const latlng: google.maps.LatLngLiteral = { lat: latitude, lng: longitude };

                const mapOptions: google.maps.MapOptions = {
                    mapId: this.mapId,
                    zoom: 8,
                    center: latlng,
                    streetViewControl: false,
                    mapTypeId: google.maps.MapTypeId.ROADMAP
                };

                this.googleMapInstance = new google.maps.Map(div, mapOptions);

                const renderer = {
                    render: ({ count, position }: any) =>
                        new google.maps.Marker({
                            label: { text: String(count), color: "white", fontSize: "12px" },
                            position,
                            icon: 'images/map-markers/ClustererMarker.svg',
                            zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
                        }),
                };
                var markers = Array.from(this.googleMapMarkerList.values());
                this.markerClusterer = new MarkerClusterer({ map: this.googleMapInstance, markers, renderer });

                if (autoHeightAdjustment) {
                    window.addEventListener('resize', this.adjustMapHeight);
                    this.adjustMapHeight();
                }

                window.addEventListener('fullscreenchange', this.handleFullscreenChange);
                resolve();
            }).catch(error => {
                console.error("Failed to load Google Maps API", error);
                reject(error);
            });
        });

        return this.mapInitializedPromise;
    }

    /**
     * Adds a custom control to the map at the specified position.
     * 
     * This method allows integrating custom UI elements into the map, enhancing interactivity
     * and providing additional functionalities to the user. The custom control is identified
     * by an element ID and placed at a defined position on the map.
     * 
     * @param itemId The ID of the DOM element to be used as the control.
     * @param position The position for the control on the map, based on google.maps.ControlPosition values.
     * @returns Promise that resolves once the control is successfully added to the map.
     */
    public async addCustomControl(itemId: string, position: string): Promise<void> {
        if (!await this.isMapInitialized()) return;

        var element = document.getElementById(itemId);
        if (!element) {
            console.warn(`The custom control item id: (${itemId}) doesn't exist.`);
            return;
        }

        const controlPosition = google.maps.ControlPosition[position as keyof typeof google.maps.ControlPosition];
        this.googleMapInstance?.controls[controlPosition].push(element);
    };

    /**
     * Sets the center of the Google Map to the specified geographic coordinates.
     * 
     * @param latitude The latitude coordinate to center the map on.
     * @param longitude The longitude coordinate to center the map on.
     * @returns A promise that resolves once the center of the map has been updated.
    */
    public async setCenter(latitude: number, longitude: number): Promise<void> {
        if (!await this.isMapInitialized()) return;

        const latlng: google.maps.LatLngLiteral = { lat: latitude, lng: longitude };
        this.googleMapInstance!.setCenter(latlng);
    }

    /**
     * Sets the zoom level of the map.
     * This method adjusts how zoomed in or out the map view is.
     *
     * @param zoom The zoom level to set the map to.
     * @returns A promise that resolves once the map's zoom level has been updated.
     */
    public async setZoom(zoom: number): Promise<void> {
        if (!await this.isMapInitialized()) return;

        this.googleMapInstance!.setZoom(zoom);
    }

    /**
     * Automatically adjusts the map view to include a specific geographical area, based on the current markers.
     * This may adjust both the center and zoom of the map to ensure all relevant points are visible.
     *
     * @returns A promise that resolves once the map's bounds have been updated to include the specified area.
     */
    public async setBounds(): Promise<void> {
        if (!await this.isMapInitialized()) return;

        const bounds = new google.maps.LatLngBounds();

        for (const polyline of this.polylinesMap.values()) {
            const path = polyline.getPath();
            for (let i = 0; i < path.getLength(); i++) {
                bounds.extend(path.getAt(i));
            }
        }

        for (const marker of this.googleMapMarkerList.values()) {
            const position = marker.position;
            if (position) {
                bounds.extend(position);
            }
        }

        if (bounds.isEmpty()) {
            return;
        }

        if (bounds.getNorthEast().equals(bounds.getSouthWest())) {
            const ne = bounds.getNorthEast();
            const offset = 0.001;
            const adjustedBounds = new google.maps.LatLngBounds(
                { lat: ne.lat() - offset, lng: ne.lng() - offset },
                { lat: ne.lat() + offset, lng: ne.lng() + offset }
            );
            this.googleMapInstance!.fitBounds(adjustedBounds);
        } else {
            this.googleMapInstance!.fitBounds(bounds);
        }
    }

    /**
      * Adds a marker to the map using the specified coordinates and control.
      * This method can be used to display points of interest, locations, or other relevant markers on the map.
      *
      * @param coord The `Coord` object containing the marker's details (e.g., position, icon).
      * @param {any} control - An object that is associated with the marker.
      * @returns A promise that resolves once the marker has been successfully added to the map.
      * 
     */
    public async setMarker(coord: Coord, control: any): Promise<void> {
        if (!await this.isMapInitialized()) return;

        var latlng = new google.maps.LatLng(coord.latitude, coord.longitude);
        var existingMarker = this.googleMapMarkerList?.get(coord.markerId);

        if (existingMarker) {
            existingMarker.textContent = coord.caption;
            existingMarker.position = latlng;
            existingMarker.icon = coord.markerIcon;
            existingMarker.content = this.getMarkerContent(coord);

            this.markerClusterer?.removeMarker(existingMarker);
            this.markerClusterer?.addMarker(existingMarker);
            if (existingMarker.draggable != coord.draggable) {
                if (coord.draggable && !this.mapClickListener) {
                    this.invokeMarkerUpdateOnMapClick(control);
                }

                existingMarker.draggable = coord.draggable;
            }

            return;
        }

        var newMarker = new google.maps.marker.AdvancedMarkerElement({
            position: latlng,
            map: this.googleMapInstance,
            content: this.getMarkerContent(coord),
            gmpDraggable: coord.draggable,
        }) as CustomMarker;
        newMarker.id = coord.markerId;
        newMarker.draggable = coord.draggable;

        this.markerClusterer?.addMarker(newMarker);

        if (newMarker.draggable && !this.mapClickListener) {
            this.invokeMarkerUpdateOnMapClick(control);
        }

        console.log(newMarker);

        newMarker.addListener("gmp-click", () => {
            if (!this.googleMapInstance) {
                return;
            }

            control.invokeMethodAsync('DisplayDetails', newMarker.id);
            const markerPosition = newMarker.position;
            if (markerPosition) {
                this.googleMapInstance.setCenter(markerPosition);
            }
        });

        if (coord.draggable == true) {
            newMarker.addListener("dragend",
                () => {
                    control.invokeMethodAsync('UpdateCoords', JSON.stringify(newMarker.position), newMarker.id);
                }
            );
        }

        this.googleMapMarkerList.set(newMarker.id, newMarker)
    }

    /**
     * Updates the map's markers to match a provided list of coordinates, potentially adding, updating, or removing markers.
     * This method ensures that the map's displayed markers accurately reflect a given set of `Coord` objects.
     *
     * @param coords An array of `Coord` objects representing the desired state of markers on the map.
     * @param control Additional control or settings applied to the markers (implementation specific).
     * @returns A promise that resolves once the markers have been synchronized with the provided coordinates.
     */
    public async syncMarkers(coords: Coord[], control: any): Promise<void> {
        if (!await this.isMapInitialized()) return;

        try {
            let validCoordIds = new Set(coords.map(item => item.markerId));
            this.googleMapMarkerList.forEach((value, key) => {
                if (!validCoordIds.has(key)) {
                    this.deleteMarker(key);
                }
            });

            coords.forEach(coord => {
                this.setMarker(coord, control)
            });
        } catch (e) {
            console.log(e);
        }
    }

    public async syncRoutes(routes: Route[]): Promise<void> {
        if (!await this.isMapInitialized()) return;

        const currentRouteIds = new Set(routes.map(r => r.id));

        for (const [id, polyline] of this.polylinesMap.entries()) {
            if (!currentRouteIds.has(id)) {
                polyline.setMap(null);
                this.polylinesMap.delete(id);
            }
        }

        for (const route of routes) {
            const path = route.path.map(p => ({ lat: p.latitude, lng: p.longitude }));

            let polyline = this.polylinesMap.get(route.id);

            if (polyline) {
                polyline.setPath(path);
                polyline.setOptions({
                    strokeColor: route.strokeColor || "#FF0000",
                    strokeOpacity: route.strokeOpacity ?? 1.0,
                    strokeWeight: route.strokeWeight ?? 1.0,
                });
            } else {
                polyline = new google.maps.Polyline({
                    path,
                    map: this.googleMapInstance!,
                    strokeColor: route.strokeColor || "#FF0000",
                    strokeOpacity: route.strokeOpacity ?? 1.0,
                    strokeWeight: route.strokeWeight ?? 2.0,
                });
                this.polylinesMap.set(route.id, polyline);
            }
        }

        await new Promise(requestAnimationFrame);
        await this.setBounds();
    }

    /**
     * Removes a specific marker from the map based on its identifier.
     * This method allows for the targeted removal of markers, making it useful for dynamic map updates.
     *
     * @param markerId The unique identifier of the marker to be removed.
     * @returns A promise that resolves once the specified marker has been successfully removed from the map.
     */
    public async deleteMarker(markerId: string): Promise<void> {
        if (!await this.isMapInitialized()) return;

        const marker = this.googleMapMarkerList.get(markerId);
        if (!marker) {
            return;
        }
        this.markerClusterer?.removeMarker(marker);
        google.maps.event.clearListeners(marker, 'dragend');
        marker.map = null;
        this.googleMapMarkerList.delete(markerId);
    }

    /**
     * Removes all markers from the map.
     * This method is useful for clearing the map of all markers, often in preparation for adding new ones or resetting the map state.
     *
     * @returns A promise that resolves once all markers have been successfully removed from the map.
     */
    public async clearMarkers(): Promise<void> {
        if (!await this.isMapInitialized()) return;

        this.googleMapMarkerList.forEach(x => {
            google.maps.event.clearListeners(x, 'dragend');
            x.map = null;
        });
        this.googleMapMarkerList.clear();
        this.markerClusterer?.clearMarkers();
    }


    public async toggleMarkersDraggable(isDraggable: boolean, control: any): Promise<void> {
        if (!await this.isMapInitialized()) return;

        this.googleMapMarkerList.forEach((marker) => {
            google.maps.event.clearListeners(marker, 'dragend');
            marker.gmpDraggable = isDraggable;
            marker.addListener("dragend",
                () => {
                    control.invokeMethodAsync('UpdateCoords', JSON.stringify(marker.position), marker.id);
                }
            );
        });
    }

    public async resetMarkerCoords(lat: number, lng: number): Promise<void> {
        if (!await this.isMapInitialized()) return;

        this.googleMapMarkerList.forEach(marker => {
            var latlng = new google.maps.LatLng(lat, lng);
            marker.position = latlng;
        });
    }

    /**
     * Cleans up the map instance and releases resources.
     * This method should be called before the map controller is discarded to ensure proper cleanup of event listeners,
     * markers, and other map-related resources to prevent memory leaks.
     */
    public dispose() {
        if (this.mapClickListener) {
            google.maps.event.removeListener(this.mapClickListener);
            this.mapClickListener = undefined;
        }

        window.removeEventListener('resize', this.adjustMapHeight);
        window.removeEventListener('fullscreenchange', this.handleFullscreenChange);
        this.clearMarkers();
    }

    /**
     * Checks if the map has been successfully initialized and is ready for use.
     * This method might check for certain conditions or flags that indicate the map's initialization state.
     * Being a private method, it's used internally within the class to ensure operations that depend on an initialized map are executed appropriately.
     *
     * @returns A promise that resolves with a boolean indicating whether the map is initialized (true) or not (false).
     */
    private async isMapInitialized(): Promise<boolean> {
        if (this.mapInitializedPromise) {
            await this.mapInitializedPromise;
        }

        if (!this.googleMapInstance) {
            console.warn(`Google Map instance (${this.mapId}) is not initialized yet.`);
            return false;
        }

        return true;
    }

    /**
     * Generates and returns a DOM Node representing the marker content for a given set of coordinates.
     *
     * @param {Coord} coord - The geographical coordinates for which the marker content is generated.
     * @returns {Node} A DOM Node representing the custom content for the map marker. This content can include
     * text, images, or any other HTML elements styled and positioned accoording to the specified coordinates.
     * 
     */
    private getMarkerContent(coord: Coord): Node {
        var markerContent;
        if (coord.markerIcon) {
            markerContent = document.createElement('img');
            markerContent.src = coord.markerIcon;

            markerContent.style.width = '30px';
            markerContent.style.height = '30px';
        }
        else {
            markerContent = document.createElement('div');
            markerContent.className = 'default-marker';

            var pinTag = new google.maps.marker.PinElement().element;
            const captionTag = document.createElement('span');
            captionTag.className = 'caption';
            captionTag.textContent = coord.caption;

            markerContent.appendChild(pinTag);
            markerContent.appendChild(captionTag);
        }

        return markerContent;
    }

    /**
     * Attaches a click event listener to the map, designed to update the position of a draggable marker
     * and synchronize these changes with a corresponding C# object.
     * When a draggable marker is moved, and the user clicks a new position on the map,
     * this method triggers the invocation of the `UpdateCoords` method on the specified C# object.
     * This allows for the seamless update of coordinates within the C# class, reflecting changes made on the map.
     * 
     * @param control The C# object that will have its `UpdateCoords` method invoked asynchronously
     * to update the marker's coordinates in the corresponding C# class. This integration enables a tight coupling
     * between the map's interactive features and the server-side logic defined in a C# application.
     */
    private invokeMarkerUpdateOnMapClick(control: any) {
        if (!this.googleMapInstance) {
            return;
        }

        if (this.mapClickListener) {
            google.maps.event.removeListener(this.mapClickListener);
        }

        this.mapClickListener = google.maps.event.addListener(
            this.googleMapInstance,
            'click',
            (event: any) => {
                if (!this.googleMapInstance) {
                    return;
                }

                const draggableMarkers = new Map([...this.googleMapMarkerList].filter(([key, marker]) => marker.draggable));
                if (!draggableMarkers || draggableMarkers.size != 1) {
                    return;
                }

                const marker = draggableMarkers.values().next().value;

                console.log(event.latLng);
                marker.position = event.latLng;
                control.invokeMethodAsync('UpdateCoords', JSON.stringify(marker.position), marker.id);

                var position = marker.position;
                if (position) {
                    this.googleMapInstance.setCenter(position);
                }
            }
        );
    }

    /**
     * Asynchronously adjusts the height of a map element based on the client height of the main content element.
     * It ensures that the map has a minimum height that is slightly less than the height of the main content,
     * specifically reduced by 25 pixels to account for any additional margins or padding that may be desired.
     *
     * @returns A Promise that resolves to void. The asynchronous nature of this function allows for potential
     * asynchronous operations within, such as checking if the map is initialized.
     */
    private async adjustMapHeight(): Promise<void> {
        if (!await this.isMapInitialized()) return;

        const mainContentElement = document.querySelector('.main-content');
        const mapElement = document.getElementById(this.mapId);

        if (mainContentElement && mapElement) {
            var mapMinHeight = mainContentElement.clientHeight - 25;
            mapElement.style.minHeight = `${mapMinHeight}px`;
        }
    }

    /**
     * Handles fullscreen changes by adjusting the map's position.
     * Sets the map's position to 'fixed' when in fullscreen mode, and 'relative' when not.
     */
    private async handleFullscreenChange(): Promise<void> {
        if (!await this.isMapInitialized()) return;

        const mapElement = document.getElementById(this.mapId) as HTMLElement | null;
        if (!mapElement) return;

        const mapFullscreenElement = mapElement.children[0] as HTMLElement | null;
        if (!mapFullscreenElement) return;

        if (document.fullscreenElement === mapFullscreenElement) {
            mapElement.style.position = 'fixed';
        } else {
            mapElement.style.position = 'relative';
        }
    }
}